import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";

// Canvas frequency-bar visualizer (DJ-style). Reads a live AnalyserNode via a ref
// in its own rAF — never triggers React re-renders per frame. Used both inside the
// orb (TTS analyser, "speaking") and below it (mic analyser, "listening").
export default function AudioBars({ analyserRef, active, color = "#fff", count = 24, sx }) {
    const canvasRef = useRef(null);
    const rafRef = useRef(null);
    const levelsRef = useRef(new Array(count).fill(0));

    useEffect(() => {
        const clear = () => {
            const c = canvasRef.current;
            if (!c) return;
            const ctx = c.getContext("2d");
            if (ctx) ctx.clearRect(0, 0, c.width, c.height);
        };
        if (!active) {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            clear();
            return undefined;
        }

        const draw = () => {
            const canvas = canvasRef.current;
            if (canvas) {
                const dpr = window.devicePixelRatio || 1;
                const cssW = canvas.clientWidth || 200;
                const cssH = canvas.clientHeight || 48;
                const tW = Math.round(cssW * dpr);
                const tH = Math.round(cssH * dpr);
                if (canvas.width !== tW || canvas.height !== tH) {
                    canvas.width = tW;
                    canvas.height = tH;
                }
                const ctx = canvas.getContext("2d");
                if (ctx) {
                    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
                    ctx.clearRect(0, 0, cssW, cssH);
                    const analyser = analyserRef && analyserRef.current;
                    const levels = levelsRef.current;
                    const gap = 3;
                    const barW = Math.max(2, (cssW - gap * (count - 1)) / count);
                    ctx.fillStyle = color;
                    if (analyser) {
                        const freq = new Uint8Array(analyser.frequencyBinCount);
                        analyser.getByteFrequencyData(freq);
                        const usable = Math.max(count, Math.floor(freq.length * 0.6));
                        const per = Math.max(1, Math.floor(usable / count));
                        for (let i = 0; i < count; i++) {
                            let sum = 0;
                            for (let j = 0; j < per; j++) sum += freq[i * per + j] || 0;
                            const target = sum / per / 255;
                            const lvl = levels[i] + (target - levels[i]) * 0.4;
                            levels[i] = lvl;
                            const h = Math.max(2, lvl * cssH);
                            const x = i * (barW + gap);
                            const y = (cssH - h) / 2;
                            ctx.globalAlpha = 0.55 + 0.45 * Math.min(1, lvl);
                            ctx.fillRect(x, y, barW, h);
                        }
                        ctx.globalAlpha = 1;
                    } else {
                        // Analyser not ready yet → decay bars toward a flat baseline.
                        for (let i = 0; i < count; i++) {
                            levels[i] *= 0.9;
                            const h = Math.max(2, levels[i] * cssH);
                            const x = i * (barW + gap);
                            const y = (cssH - h) / 2;
                            ctx.fillRect(x, y, barW, h);
                        }
                    }
                }
            }
            rafRef.current = requestAnimationFrame(draw);
        };
        rafRef.current = requestAnimationFrame(draw);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        };
    }, [active, analyserRef, color, count]);

    return <Box component="canvas" ref={canvasRef} aria-hidden="true" sx={{ display: "block", width: "100%", height: 48, ...sx }} />;
}
